import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { PublicApi } from '../constants';
import { environment } from '../../environments/environment';

// Xano's standard file-resource shape — the uploaded attachment itself.
export interface UploadedModel {
  access?: string;
  path?: string;
  name?: string;
  type?: string;
  size?: number;
  mime?: string;
  meta?: Record<string, any>;
  url?: string;
}

// The saved record returned by /upload/model and listed by /my_models — it wraps the file.
export interface ModelRecord {
  id?: number;
  created_at?: number;
  user_id?: number;
  created_by?: number;
  name?: string;
  model?: UploadedModel;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class Model3dService {
  private http = inject(HttpClient);

  // multipart/form-data: the file goes in `content`, the display name in `name`.
  // Content-Type is deliberately left unset so the browser adds the multipart boundary itself.
  uploadModel(file: File, name = ''): Observable<HttpEvent<ModelRecord>> {
    const formData = new FormData();
    formData.append('content', file, file.name);
    formData.append('name', name);

    return this.http.post<ModelRecord>(PublicApi.UPLOAD_MODEL, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  getMyModels(): Observable<any> {
    return this.http.get<any>(PublicApi.MY_MODELS);
  }

  /* ------------------------------------------------------------------ */
  /*                     Response shape helpers                         */
  /* ------------------------------------------------------------------ */

  // /my_models returns either a bare array or a paginated envelope.
  toRecordList(res: any): ModelRecord[] {
    return Array.isArray(res) ? res : res?.items || res?.data || [];
  }

  // A record wraps the file resource (e.g. { id, name, model: {...} }), so name/size/mime
  // have to be read from the nested object, not the root.
  getFileObject(source: any, depth = 0): UploadedModel | null {
    if (!source || typeof source !== 'object' || depth > 4) return null;

    if (Array.isArray(source)) {
      for (const entry of source) {
        const found = this.getFileObject(entry, depth + 1);
        if (found) return found;
      }
      return null;
    }

    if (typeof source['path'] === 'string' && source['path']) {
      return source as UploadedModel;
    }

    for (const value of Object.values(source)) {
      const found = this.getFileObject(value, depth + 1);
      if (found) return found;
    }

    return null;
  }

  // Xano returns either an absolute `url` or a vault `path` — normalize both to a usable link.
  getModelUrl(source: any): string {
    const candidate = this.findUrlCandidate(source);
    if (!candidate) return '';
    if (/^https?:\/\//i.test(candidate)) return candidate;

    const origin = environment.apiUrl.replace(/\/api\/?$/, '');
    return `${origin}${candidate.startsWith('/') ? '' : '/'}${candidate}`;
  }

  private findUrlCandidate(source: any, depth = 0): string {
    if (!source || depth > 4) return '';

    if (typeof source === 'string') {
      // Only strings that actually look like a link — not names, mime types, etc.
      return /^https?:\/\//i.test(source) || source.startsWith('/') ? source : '';
    }

    if (Array.isArray(source)) {
      for (const entry of source) {
        const found = this.findUrlCandidate(entry, depth + 1);
        if (found) return found;
      }
      return '';
    }

    if (typeof source === 'object') {
      for (const key of ['url', 'path']) {
        const value = source[key];
        if (typeof value === 'string' && value) return value;
      }
      for (const value of Object.values(source)) {
        const found = this.findUrlCandidate(value, depth + 1);
        if (found) return found;
      }
    }

    return '';
  }
}
